import type { TableInfo, TablePermission, TablePermissionUser, TableResponse } from "$lib/types";
import { APIError } from "$lib/types/api";
import posthog from "posthog-js";

export class TableAPI {
  private static readonly BASE_URL = "/api/v1/table";
  private static readonly ENDPOINTS = {
    getAllTables: "",
    getTableInfo: (id: string) => `/get_table_info/${id}`,
    createTable: "/create",
    updateTable: "/update",
    updateTeamAndPublicPermission: "/update_team_and_public_permission",
    getTablePermissions: (id: string) => `/permissions/${id}`,
    setTablePermission: "/permissions/set",
    removeTablePermission: "/permissions/remove",
    deleteTable: (id: string) => `/delete/${id}`,
  } as const;

  private static readonly HEADERS = {
    contentType: "Content-Type",
    jsonContentType: "application/json",
  } as const;

  /**
   * Gets all tables
   * @throws Error if the fetch request fails
   */
  static async getAllTables(): Promise<TableInfo[]> {
    try {
      const response = await fetch(this.BASE_URL, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status >= 400 && response.status < 500) {
          const errorData = await response.json();
          throw new APIError(errorData.message || response.statusText, true);
        }
        throw new APIError(`Server error: ${response.statusText}`, false);
      }

      const data = (await response.json()) as TableResponse;
      return data.tables.map((table) => ({
        ...table,
        createdAt: new Date(table.createdAt),
        updatedAt: new Date(table.updatedAt),
      }));
    } catch (error) {
      posthog.capture("api_error", {
        category: "api",
        message: "Failed to get all tables",
        level: "error",
        data: {
          endpoint: this.BASE_URL,
        },
      });

      if (error instanceof APIError && !error.isExpected) {
        posthog.captureException(error);
      }

      console.error("Get all tables error:", error);
      throw error;
    }
  }

  /**
   * Gets table information by ID
   * @throws Error if the fetch request fails
   */
  static async getTableInfo(id: string): Promise<{
    exists: boolean;
    name: string;
    visibility: string;
    accessLevel: string;
    publicPermission: string;
    teamPermission: string;
  }> {
    try {
      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.getTableInfo(id)}`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status >= 400 && response.status < 500) {
          const errorData = await response.json();
          throw new APIError(errorData.message || response.statusText, true);
        }
        throw new APIError(`Server error: ${response.statusText}`, false);
      }

      return await response.json();
    } catch (error) {
      posthog.capture("api_error", {
        category: "api",
        message: "Failed to get table info",
        level: "error",
        data: {
          tableId: id,
        },
      });

      if (error instanceof APIError && !error.isExpected) {
        posthog.captureException(error);
      }

      console.error("Get table info error:", error);
      throw error;
    }
  }

  /**
   * Creates a new table
   * @throws Error if the fetch request fails
   */
  static async createTable(params: {
    name: string;
    numColumns: number;
    numRows: number;
    templateId?: string;
  }): Promise<{ id: string }> {
    try {
      const formData = new URLSearchParams();
      formData.append("name", params.name);
      formData.append("numColumns", params.numColumns.toString());
      formData.append("numRows", params.numRows.toString());
      if (params.templateId) {
        formData.append("templateID", params.templateId);
      }

      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.createTable}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: "application/x-www-form-urlencoded",
        },
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status >= 400 && response.status < 500) {
          const errorData = await response.json();
          throw new APIError(errorData.message || response.statusText, true);
        }
        throw new APIError(`Server error: ${response.statusText}`, false);
      }

      return await response.json();
    } catch (error) {
      posthog.capture("api_error", {
        category: "api",
        message: "Failed to create table",
        level: "error",
        data: {
          tableName: params.name,
          dimensions: `${params.numColumns}x${params.numRows}`,
          templateId: params.templateId,
        },
      });

      if (error instanceof APIError && !error.isExpected) {
        posthog.captureException(error);
      }

      console.error("Create table error:", error);
      throw error;
    }
  }

  /**
   * Updates a table's information
   * @throws Error if the fetch request fails
   */
  static async updateTable(params: { id: string; name: string }): Promise<void> {
    try {
      const formData = new URLSearchParams();
      formData.append("id", params.id);
      formData.append("name", params.name);

      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.updateTable}`, {
        method: "PUT",
        headers: {
          [this.HEADERS.contentType]: "application/x-www-form-urlencoded",
        },
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status >= 400 && response.status < 500) {
          const errorData = await response.json();
          logger.log("statusText", response.statusText);
          throw new APIError(errorData.message || response.statusText, true);
        }
        throw new APIError(`Server error: ${response.statusText}`, false);
      }
    } catch (error) {
      posthog.capture("api_error", {
        category: "api",
        message: "Failed to update table",
        level: "error",
        data: {
          tableId: params.id,
          tableName: params.name,
        },
      });

      if (error instanceof APIError && !error.isExpected) {
        posthog.captureException(error);
      }

      console.error("Update table error:", error);
      throw error;
    }
  }

  /**
   * Updates a table's visibility
   * @throws Error if the fetch request fails
   */
  static async updateTeamAndPublicPermission(params: {
    tableId: string;
    teamPermission: string;
    publicPermission: string;
  }): Promise<void> {
    try {
      const formData = new URLSearchParams();
      formData.append("tableId", params.tableId);
      formData.append("teamPermission", params.teamPermission);
      formData.append("publicPermission", params.publicPermission);

      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.updateTeamAndPublicPermission}`, {
        method: "PUT",
        headers: {
          [this.HEADERS.contentType]: "application/x-www-form-urlencoded",
        },
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status >= 400 && response.status < 500) {
          const errorData = await response.json();
          throw new APIError(errorData.message || response.statusText, true);
        }
        throw new APIError(`Server error: ${response.statusText}`, false);
      }
    } catch (error) {
      posthog.capture("api_error", {
        category: "api",
        message: "Failed to update visibility",
        level: "error",
        data: {
          tableId: params.tableId,
          teamPermission: params.teamPermission,
          publicPermission: params.publicPermission,
        },
      });

      if (error instanceof APIError && !error.isExpected) {
        posthog.captureException(error);
      }

      console.error("Update visibility error:", error);
      throw error;
    }
  }

  /**
   * Deletes a table by ID
   * @throws Error if the fetch request fails
   */
  static async deleteTable(id: string): Promise<void> {
    try {
      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.deleteTable(id)}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status >= 400 && response.status < 500) {
          const errorData = await response.json();
          throw new APIError(errorData.message || response.statusText, true);
        }
        throw new APIError(`Server error: ${response.statusText}`, false);
      }
    } catch (error) {
      posthog.capture("api_error", {
        category: "api",
        message: "Failed to delete table",
        level: "error",
        data: {
          tableId: id,
        },
      });

      if (error instanceof APIError && !error.isExpected) {
        posthog.captureException(error);
      }

      console.error("Delete table error:", error);
      throw error;
    }
  }

  /**
   * Gets the permissions for a table
   * @throws Error if the fetch request fails
   */
  static async getTablePermissions(id: string): Promise<{ permissions: TablePermissionUser[] }> {
    const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.getTablePermissions(id)}`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      throw new APIError(`Server error: ${response.statusText}`, false);
    }

    return await response.json();
  }

  /**
   * Adds a table permission
   * @throws Error if the fetch request fails
   */
  static async addTablePermission(params: {
    tableId: string;
    email: string;
    permission: TablePermission;
  }): Promise<{ success: boolean }> {
    console.log("params", params);
    const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.setTablePermission}`, {
      method: "POST",
      headers: {
        [this.HEADERS.contentType]: "application/json",
      },
      body: JSON.stringify(params),
      credentials: "include",
    });

    if (!response.ok) {
      throw new APIError(`Server error: ${response.statusText}`, false);
    }

    return await response.json();
  }

  /**
   * Removes a table permission
   * @throws Error if the fetch request fails
   */
  static async removeTablePermission(params: { tableId: string; userId: string }): Promise<{
    success: boolean;
  }> {
    const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.removeTablePermission}`, {
      method: "POST",
      headers: {
        [this.HEADERS.contentType]: "application/json",
      },
      body: JSON.stringify(params),
      credentials: "include",
    });

    if (!response.ok) {
      throw new APIError(`Server error: ${response.statusText}`, false);
    }

    return await response.json();
  }
}
